/**
 * Gongfu 插件系统
 * 提供可扩展的插件架构，支持第三方开发者扩展功能
 */

import { EventEmitter } from 'events';
import { Command } from 'commander';
import * as fs from 'fs-extra';
import * as path from 'path';
import { logger } from '../utils/logger';
import { ExtendedTask } from '../types/extended-task';
import { Agent } from './agent-manager';
import { PromptTemplate } from './prompt-template-engine';

/**
 * 插件元数据
 */
export interface PluginMetadata {
  name: string;
  version: string;
  description: string;
  author?: string;
  homepage?: string;
  repository?: string;
  keywords?: string[];
  engines?: {
    node?: string;
    gongfu?: string;
  };
  dependencies?: Record<string, string>;
  permissions?: PluginPermission[];
}

/**
 * 插件权限
 */
export type PluginPermission = 
  | 'tasks:read'      // 读取任务
  | 'tasks:write'     // 修改任务
  | 'agents:use'      // 使用 Agent
  | 'agents:create'   // 创建 Agent
  | 'files:read'      // 读取文件
  | 'files:write'     // 写入文件
  | 'network:http'    // HTTP 请求
  | 'system:exec'     // 执行系统命令
  | 'ui:register'     // 注册 UI 组件
  | 'api:extend';     // 扩展 API

/**
 * 插件生命周期钩子
 */
export interface PluginHooks {
  /**
   * 插件加载时调用
   */
  onLoad?: () => Promise<void>;
  
  /**
   * 插件激活时调用
   */
  onActivate?: () => Promise<void>;
  
  /**
   * 插件停用时调用
   */
  onDeactivate?: () => Promise<void>;
  
  /**
   * 插件卸载时调用
   */
  onUnload?: () => Promise<void>;
  
  /**
   * 任务创建前调用
   */
  beforeTaskCreate?: (task: Partial<ExtendedTask>) => Promise<Partial<ExtendedTask>>;
  
  /**
   * 任务创建后调用
   */
  afterTaskCreate?: (task: ExtendedTask) => Promise<void>;
  
  /**
   * 任务更新前调用
   */
  beforeTaskUpdate?: (id: string, updates: Partial<ExtendedTask>) => Promise<Partial<ExtendedTask>>;
  
  /**
   * 任务更新后调用
   */
  afterTaskUpdate?: (task: ExtendedTask) => Promise<void>;
  
  /**
   * Agent 执行前调用
   */
  beforeAgentExecute?: (agent: Agent, task: ExtendedTask) => Promise<void>;
  
  /**
   * Agent 执行后调用
   */
  afterAgentExecute?: (agent: Agent, task: ExtendedTask, result: any) => Promise<any>;
}

/**
 * 插件 API 接口
 */
export interface PluginAPI {
  /**
   * 获取任务
   */
  getTasks(): Promise<ExtendedTask[]>;
  
  /**
   * 获取单个任务
   */
  getTask(id: string): Promise<ExtendedTask | null>;
  
  /**
   * 创建任务
   */
  createTask(task: Partial<ExtendedTask>): Promise<ExtendedTask>;
  
  /**
   * 更新任务
   */
  updateTask(id: string, updates: Partial<ExtendedTask>): Promise<ExtendedTask>;
  
  /**
   * 获取 Agent
   */
  getAgents(): Agent[];
  
  /**
   * 注册 Agent
   */
  registerAgent(agent: Agent): void;
  
  /**
   * 获取 Prompt 模板
   */
  getPromptTemplates(): PromptTemplate[];
  
  /**
   * 注册 Prompt 模板
   */
  registerPromptTemplate(template: PromptTemplate): void;
  
  /**
   * 注册命令
   */
  registerCommand(command: Command): void;
  
  /**
   * 发送通知
   */
  notify(message: string, type?: 'info' | 'success' | 'warning' | 'error'): void;
  
  /**
   * 获取配置
   */
  getConfig(key: string): any;
  
  /**
   * 设置配置
   */
  setConfig(key: string, value: any): void;
  
  /**
   * 注册事件监听
   */
  on(event: string, handler: (...args: any[]) => void): void;
  
  /**
   * 触发事件
   */
  emit(event: string, ...args: any[]): void;
  
  /**
   * 日志记录
   */
  logger: {
    debug: (message: string, ...args: any[]) => void;
    info: (message: string, ...args: any[]) => void;
    warn: (message: string, ...args: any[]) => void;
    error: (message: string, ...args: any[]) => void;
  };
}

/**
 * 插件接口
 */
export interface Plugin {
  metadata: PluginMetadata;
  hooks?: PluginHooks;
  
  /**
   * 插件初始化
   */
  init(api: PluginAPI): Promise<void>;
  
  /**
   * 插件清理
   */
  cleanup?(): Promise<void>;
}

/**
 * 插件实例
 */
export interface PluginInstance {
  plugin: Plugin;
  metadata: PluginMetadata;
  enabled: boolean;
  loaded: boolean;
  path: string;
  permissions: PluginPermission[];
}

/**
 * 插件配置
 */
export interface PluginConfig {
  enabled: boolean;
  autoLoad: boolean;
  searchPaths: string[];
  registry?: string;
  allowedPermissions?: PluginPermission[];
  blacklist?: string[];
  whitelist?: string[];
}

/**
 * 插件管理器
 */
export class PluginManager extends EventEmitter {
  private plugins: Map<string, PluginInstance> = new Map();
  private config: PluginConfig;
  private api: PluginAPI;
  private pluginsDir: string;
  private configPath: string;
  
  constructor(
    private workflowDir: string = process.cwd()
  ) {
    super();
    this.pluginsDir = path.join(workflowDir, '.workflow', 'plugins');
    this.configPath = path.join(workflowDir, '.workflow', 'plugins.json');
    
    // 默认配置
    this.config = {
      enabled: true,
      autoLoad: true,
      searchPaths: [
        this.pluginsDir,
        path.join(workflowDir, 'plugins'),
        path.join(workflowDir, 'node_modules')
      ],
      registry: 'https://registry.gongfu.dev',
      allowedPermissions: [
        'tasks:read',
        'tasks:write',
        'agents:use',
        'files:read',
        'network:http',
        'ui:register',
        'api:extend'
      ]
    };
    
    // 初始化 API
    this.api = this.createPluginAPI();
  }
  
  /**
   * 初始化插件系统
   */
  async initialize(): Promise<void> {
    // 确保插件目录存在
    await fs.ensureDir(this.pluginsDir);
    
    // 加载配置
    await this.loadConfig();
    
    if (!this.config.enabled) {
      logger.info('Plugin system is disabled');
      return;
    }
    
    // 扫描和加载插件
    if (this.config.autoLoad) {
      await this.scanAndLoadPlugins();
    }
    
    logger.info(`Plugin system initialized with ${this.plugins.size} plugins`);
  }
  
  /**
   * 加载配置
   */
  private async loadConfig(): Promise<void> {
    try {
      if (await fs.pathExists(this.configPath)) {
        const data = await fs.readJson(this.configPath);
        this.config = { ...this.config, ...data };
      }
    } catch (error) {
      logger.error('Failed to load plugin config:', error);
    }
  }
  
  /**
   * 保存配置
   */
  private async saveConfig(): Promise<void> {
    try {
      await fs.writeJson(this.configPath, this.config, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save plugin config:', error);
    }
  }
  
  /**
   * 创建插件 API
   */
  private createPluginAPI(): PluginAPI {
    const self = this;
    
    return {
      // 任务相关
      getTasks: async () => {
        // 这里应该调用实际的 TaskStore
        return [];
      },
      
      getTask: async (id: string) => {
        // 这里应该调用实际的 TaskStore
        return null;
      },
      
      createTask: async (task: Partial<ExtendedTask>) => {
        self.checkPermission('tasks:write');
        // 触发钩子
        const modifiedTask = await self.runHook('beforeTaskCreate', task);
        // 创建任务
        const newTask = {} as ExtendedTask; // 实际创建
        await self.runHook('afterTaskCreate', newTask);
        return newTask;
      },
      
      updateTask: async (id: string, updates: Partial<ExtendedTask>) => {
        self.checkPermission('tasks:write');
        const modifiedUpdates = await self.runHook('beforeTaskUpdate', id, updates);
        const updatedTask = {} as ExtendedTask; // 实际更新
        await self.runHook('afterTaskUpdate', updatedTask);
        return updatedTask;
      },
      
      // Agent 相关
      getAgents: () => {
        return [];
      },
      
      registerAgent: (agent: Agent) => {
        self.checkPermission('agents:create');
        // 注册 Agent
      },
      
      // Prompt 模板相关
      getPromptTemplates: () => {
        return [];
      },
      
      registerPromptTemplate: (template: PromptTemplate) => {
        // 注册模板
      },
      
      // 命令相关
      registerCommand: (command: Command) => {
        self.checkPermission('api:extend');
        // 注册命令
      },
      
      // 通知相关
      notify: (message: string, type = 'info') => {
        logger[type](message);
        self.emit('notification', { message, type });
      },
      
      // 配置相关
      getConfig: (key: string) => {
        return self.getPluginConfig(key);
      },
      
      setConfig: (key: string, value: any) => {
        self.setPluginConfig(key, value);
      },
      
      // 事件相关
      on: (event: string, handler: (...args: any[]) => void) => {
        self.on(`plugin:${event}`, handler);
      },
      
      emit: (event: string, ...args: any[]) => {
        self.emit(`plugin:${event}`, ...args);
      },
      
      // 日志相关
      logger: {
        debug: (message: string, ...args: any[]) => logger.debug(`[Plugin] ${message}`, ...args),
        info: (message: string, ...args: any[]) => logger.info(`[Plugin] ${message}`, ...args),
        warn: (message: string, ...args: any[]) => logger.warn(`[Plugin] ${message}`, ...args),
        error: (message: string, ...args: any[]) => logger.error(`[Plugin] ${message}`, ...args)
      }
    };
  }
  
  /**
   * 检查权限
   */
  private checkPermission(permission: PluginPermission): void {
    if (!this.config.allowedPermissions?.includes(permission)) {
      throw new Error(`Permission denied: ${permission}`);
    }
  }
  
  /**
   * 扫描并加载插件
   */
  async scanAndLoadPlugins(): Promise<void> {
    for (const searchPath of this.config.searchPaths) {
      if (await fs.pathExists(searchPath)) {
        await this.scanDirectory(searchPath);
      }
    }
    
    // 自动启用白名单中的插件
    if (this.config.whitelist) {
      for (const pluginName of this.config.whitelist) {
        const instance = this.plugins.get(pluginName);
        if (instance && !instance.enabled) {
          await this.enablePlugin(pluginName);
        }
      }
    }
  }
  
  /**
   * 扫描目录
   */
  private async scanDirectory(dir: string): Promise<void> {
    try {
      const entries = await fs.readdir(dir, { withFileTypes: true });
      
      for (const entry of entries) {
        if (entry.isDirectory()) {
          const pluginPath = path.join(dir, entry.name);
          const packagePath = path.join(pluginPath, 'package.json');
          
          if (await fs.pathExists(packagePath)) {
            await this.loadPlugin(pluginPath);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to scan directory ${dir}:`, error);
    }
  }
  
  /**
   * 加载插件
   */
  async loadPlugin(pluginPath: string): Promise<boolean> {
    try {
      // 读取 package.json
      const packagePath = path.join(pluginPath, 'package.json');
      const packageData = await fs.readJson(packagePath);
      
      // 检查是否是 Gongfu 插件
      if (!packageData.gongfu) {
        return false;
      }
      
      const metadata: PluginMetadata = {
        name: packageData.name,
        version: packageData.version,
        description: packageData.description,
        author: packageData.author,
        homepage: packageData.homepage,
        repository: packageData.repository,
        keywords: packageData.keywords,
        engines: packageData.engines,
        dependencies: packageData.dependencies,
        permissions: packageData.gongfu.permissions || []
      };
      
      // 检查黑名单
      if (this.config.blacklist?.includes(metadata.name)) {
        logger.warn(`Plugin ${metadata.name} is blacklisted`);
        return false;
      }
      
      // 检查权限
      const deniedPermissions = metadata.permissions?.filter(
        p => !this.config.allowedPermissions?.includes(p)
      );
      
      if (deniedPermissions && deniedPermissions.length > 0) {
        logger.warn(`Plugin ${metadata.name} requires denied permissions: ${deniedPermissions.join(', ')}`);
        return false;
      }
      
      // 加载插件模块
      const mainFile = packageData.main || 'index.js';
      const modulePath = path.join(pluginPath, mainFile);
      
      // 清除缓存
      delete require.cache[require.resolve(modulePath)];
      
      // 加载模块
      const PluginClass = require(modulePath).default || require(modulePath);
      const plugin: Plugin = new PluginClass();
      
      // 创建插件实例
      const instance: PluginInstance = {
        plugin,
        metadata,
        enabled: false,
        loaded: true,
        path: pluginPath,
        permissions: metadata.permissions || []
      };
      
      // 注册插件
      this.plugins.set(metadata.name, instance);
      
      // 触发加载钩子
      if (plugin.hooks?.onLoad) {
        await plugin.hooks.onLoad();
      }
      
      // 初始化插件
      await plugin.init(this.api);
      
      logger.info(`Loaded plugin: ${metadata.name} v${metadata.version}`);
      
      // 触发事件
      this.emit('plugin:loaded', metadata);
      
      return true;
    } catch (error) {
      logger.error(`Failed to load plugin from ${pluginPath}:`, error);
      return false;
    }
  }
  
  /**
   * 启用插件
   */
  async enablePlugin(name: string): Promise<void> {
    const instance = this.plugins.get(name);
    if (!instance) {
      throw new Error(`Plugin not found: ${name}`);
    }
    
    if (instance.enabled) {
      return;
    }
    
    // 触发激活钩子
    if (instance.plugin.hooks?.onActivate) {
      await instance.plugin.hooks.onActivate();
    }
    
    instance.enabled = true;
    
    logger.info(`Enabled plugin: ${name}`);
    this.emit('plugin:enabled', instance.metadata);
  }
  
  /**
   * 禁用插件
   */
  async disablePlugin(name: string): Promise<void> {
    const instance = this.plugins.get(name);
    if (!instance) {
      throw new Error(`Plugin not found: ${name}`);
    }
    
    if (!instance.enabled) {
      return;
    }
    
    // 触发停用钩子
    if (instance.plugin.hooks?.onDeactivate) {
      await instance.plugin.hooks.onDeactivate();
    }
    
    instance.enabled = false;
    
    logger.info(`Disabled plugin: ${name}`);
    this.emit('plugin:disabled', instance.metadata);
  }
  
  /**
   * 卸载插件
   */
  async unloadPlugin(name: string): Promise<void> {
    const instance = this.plugins.get(name);
    if (!instance) {
      throw new Error(`Plugin not found: ${name}`);
    }
    
    // 先禁用
    if (instance.enabled) {
      await this.disablePlugin(name);
    }
    
    // 触发卸载钩子
    if (instance.plugin.hooks?.onUnload) {
      await instance.plugin.hooks.onUnload();
    }
    
    // 清理
    if (instance.plugin.cleanup) {
      await instance.plugin.cleanup();
    }
    
    // 移除插件
    this.plugins.delete(name);
    
    logger.info(`Unloaded plugin: ${name}`);
    this.emit('plugin:unloaded', instance.metadata);
  }
  
  /**
   * 运行钩子
   */
  private async runHook(hookName: keyof PluginHooks, ...args: any[]): Promise<any> {
    let result = args[0];
    
    for (const [name, instance] of this.plugins) {
      if (!instance.enabled) continue;
      
      const hook = instance.plugin.hooks?.[hookName];
      if (hook && typeof hook === 'function') {
        try {
          result = await hook(...args);
        } catch (error) {
          logger.error(`Plugin ${name} hook ${hookName} failed:`, error);
        }
      }
    }
    
    return result;
  }
  
  /**
   * 获取插件列表
   */
  getPlugins(): PluginInstance[] {
    return Array.from(this.plugins.values());
  }
  
  /**
   * 获取插件
   */
  getPlugin(name: string): PluginInstance | undefined {
    return this.plugins.get(name);
  }
  
  /**
   * 安装插件
   */
  async installPlugin(nameOrUrl: string): Promise<void> {
    // TODO: 实现从 npm 或 URL 安装插件
    logger.info(`Installing plugin: ${nameOrUrl}`);
  }
  
  /**
   * 更新插件
   */
  async updatePlugin(name: string): Promise<void> {
    // TODO: 实现插件更新
    logger.info(`Updating plugin: ${name}`);
  }
  
  /**
   * 搜索插件
   */
  async searchPlugins(query: string): Promise<any[]> {
    // TODO: 实现从注册中心搜索插件
    logger.info(`Searching plugins: ${query}`);
    return [];
  }
  
  /**
   * 获取插件配置
   */
  private getPluginConfig(key: string): any {
    // TODO: 实现插件配置管理
    return null;
  }
  
  /**
   * 设置插件配置
   */
  private setPluginConfig(key: string, value: any): void {
    // TODO: 实现插件配置管理
  }
}

/**
 * 创建插件（用于插件开发）
 */
export function createPlugin(options: {
  metadata: PluginMetadata;
  hooks?: PluginHooks;
  init: (api: PluginAPI) => Promise<void>;
  cleanup?: () => Promise<void>;
}): Plugin {
  return {
    metadata: options.metadata,
    hooks: options.hooks,
    init: options.init,
    cleanup: options.cleanup
  };
}